import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Divider,
  FormControl,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
  useDisclosure
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  memo,
  useState
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { callToGetStoreNames } from '@/queue/ducks/slice';
import { getStoreNamesData } from '@/queue/ducks/selectors';
import Select from 'react-select';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  IGetRowsParams
} from 'ag-grid-community';
import { agGridIconsConfig } from '@/explore/ffl-detail/utils.mjs';
import AddEventModal from './AddEventModal';
import EventLogsDataSource, {
  EVENT_LOGS_PAGE_SIZE,
  type EventLogsFilterParams
} from './DealerLogsDataSource';
import { updateEventLogVisibility } from '@/api/apis';
import {
  SimpleToasterNotification,
  ErrorToasterNotification
} from '@/toaster/successNotification';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ChevronRightIcon } from '@chakra-ui/icons';
import FlatDatePickr from '@/common/Flatpickr';
import {
  INVALID_RANGE,
  SELECT_FIELD_DATE,
  dealerLogActionTypeFilter
} from '@/utils/constants';
import { getIsImpersonating } from '@/redux/app-selectors';
import { RenderItems } from '@/explore/ffl-detail/licenseByZipQuery/filters/Filters';
import { keyType } from '@/queue/types';
import * as constants from '@/utils/constants';

export interface DealerLogRow {
  id?: number;
  transferId: string;
  orderNumber: string;
  time: string;
  updatedBy: string;
  action: 'Called' | 'Emailed' | 'Profile Edit' | 'System' | 'Other';
  notes: string;
  store: string;
  isHidden?: boolean;
}

const PAGE_SIZE = EVENT_LOGS_PAGE_SIZE;

function formatDateToYYYYMMDD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseYYYYMMDDToDate(s: string | undefined): Date | undefined {
  if (!s?.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(s.trim())) return undefined;
  const [y, m, d] = s.trim().split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? undefined : date;
}

const EMPTY_PLACEHOLDER = '—';

/** Format as MM/DD/YYYY hh:mm AM/PM e.g. 09/30/2025 03:28 PM */
function formatTime(value: string): string {
  if (!value) return EMPTY_PLACEHOLDER;
  const d = new Date(value);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  const hours24 = d.getHours();
  const hours12 = hours24 % 12 || 12;
  const hourStr = String(hours12).padStart(2, '0');
  const minuteStr = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours24 < 12 ? 'AM' : 'PM';
  return `${month}/${day}/${year} ${hourStr}:${minuteStr} ${ampm}`;
}

// Renders transfer ID as link when present; Loading when value not yet available; "—" when null/empty
const TransferLinkCell = memo(function TransferLinkCell({
  value
}: {
  value?: string;
}) {
  if (value === undefined) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <img src="https://www.ag-grid.com/example-assets/loading.gif" alt="" />
        &nbsp; Loading
      </Box>
    );
  }
  if (value == null || String(value).trim() === '') return <>{EMPTY_PLACEHOLDER}</>;
  const trimmed = String(value).trim();
  return (
    <a
      href={`/transfers/${trimmed}`}
      className="dealer-logs-transfer-link"
      target="_blank"
      rel="noopener noreferrer"
    >
      {trimmed}
    </a>
  );
});

const ACTION_STYLES: Record<string, { className: string }> = {
  Called: { className: 'dealer-logs-action-badge dealer-logs-action-called' },
  Emailed: { className: 'dealer-logs-action-badge dealer-logs-action-emailed' },
  'Profile Edit': {
    className: 'dealer-logs-action-badge dealer-logs-action-profile-edit'
  },
  System: { className: 'dealer-logs-action-badge dealer-logs-action-system' },
  Other: { className: 'dealer-logs-action-badge dealer-logs-action-other' }
};

const ACTION_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'called', label: 'Called' },
  { value: 'emailed', label: 'Emailed' },
  { value: 'profile edit', label: 'Profile Edit' },
  { value: 'system', label: 'System' },
  { value: 'other', label: 'Other' }
];

const ActionBadgeCell = memo(function ActionBadgeCell({
  value
}: {
  value?: DealerLogRow['action'];
}) {
  if (!value) return <>{EMPTY_PLACEHOLDER}</>;
  const style = ACTION_STYLES[value] ?? ACTION_STYLES.Other;
  return <span className={style.className}>{value}</span>;
});

const HIDE_UNHIDE_COL_ID = 'hideUnhideAction';

const HideUnhideActionCell = memo(function HideUnhideActionCell(props: {
  data?: DealerLogRow;
  colDef?: {
    cellRendererParams?: {
      getOnToggle?: () => (row: DealerLogRow) => void;
      loadingId?: number | null;
    };
  };
}) {
  const { data, colDef } = props;
  const onToggle = colDef?.cellRendererParams?.getOnToggle?.();
  const loadingId = colDef?.cellRendererParams?.loadingId ?? null;
  const isHidden = data?.isHidden ?? false;
  const isLoading = data?.id != null && data.id === loadingId;
  const handleClick = useCallback(() => {
    if (data && !isLoading) onToggle?.(data);
  }, [data, onToggle, isLoading]);
  if (!data) return null;
  return (
    <Button
      size="sm"
      variant={isHidden ? 'outline' : 'solid'}
      colorScheme="blue"
      onClick={handleClick}
      isLoading={isLoading}
      loadingText=""
      isDisabled={isLoading}
      className={`dealer-logs-hide-unhide-btn ${
        isHidden ? 'dealer-logs-unhide-btn' : ''
      }`}
    >
      {isHidden ? 'Unhide' : 'Hide'}
    </Button>
  );
});

// Notes cell: tooltip only when note is long (truncated/ellipsed), never for short notes
const NotesCell = memo(function NotesCell({ value }: { value?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const checkTruncated = useCallback(() => {
    const el = ref.current;
    if (!el || value == null) return;
    setShowTooltip(el.scrollWidth > el.clientWidth);
  }, [value]);

  useLayoutEffect(() => {
    checkTruncated();
  }, [checkTruncated]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(checkTruncated);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkTruncated]);

  if (value == null || value === '' || value === EMPTY_PLACEHOLDER)
    return <>{EMPTY_PLACEHOLDER}</>;
  const inner = (
    <span ref={ref} className="dealer-logs-notes-cell-inner">
      {value}
    </span>
  );
  if (!showTooltip) return inner;
  return (
    <Tooltip hasArrow placement="bottom" label={value} color="black" bg="white">
      {inner}
    </Tooltip>
  );
});

const DealerLogs = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const gridRef = useRef<AgGridReact<DealerLogRow>>(null);
  const gridStyle = useMemo(
    () => ({ width: '100%', height: '100%', minHeight: 400 }),
    []
  );
  const {
    isOpen: isAddEventOpen,
    onOpen: onOpenAddEvent,
    onClose: onCloseAddEvent
  } = useDisclosure();
  const {
    isOpen: isFilterOpen,
    onOpen: onOpenFilter,
    onClose: onCloseFilter
  } = useDisclosure();

  const initialLicenseKey = searchParams.get('licenseKey') ?? undefined;
  const initialTransferId = searchParams.get('transferId') ?? undefined;
  const initialDealerId = searchParams.get('dealerId') ?? undefined;
  const isImpersonating = getIsImpersonating();

  /** When opened from transfer details (dealerId in URL), keep that dealer on every filter update. */
  const withUrlDealerId = useCallback(
    (params: EventLogsFilterParams): EventLogsFilterParams => {
      const d = initialDealerId?.trim();
      return d ? { ...params, dealerId: d } : params;
    },
    [initialDealerId]
  );

  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<EventLogsFilterParams>(() => {
    const initial: EventLogsFilterParams = {};
    if (initialTransferId?.trim())
      initial.transfer_id = initialTransferId.trim();
    if (initialDealerId?.trim()) initial.dealerId = initialDealerId.trim();
    return initial;
  });
  const [dateRangeError, setDateRangeError] = useState('');
  const [datePickerContainer, setDatePickerContainer] =
    useState<HTMLElement | null>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const storeOptions = getStoreNamesData() ?? [];

  const [loadingHideUnhideId, setLoadingHideUnhideId] = useState<number | null>(
    null
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingHideUnhideRef = useRef<number | null>(null);
  loadingHideUnhideRef.current = loadingHideUnhideId;
  const onHideUnhideRef = useRef<(row: DealerLogRow) => void>(() => {});
  const handleHideUnhide = useCallback(async (row: DealerLogRow) => {
    if (row.id == null) {
      ErrorToasterNotification('This event cannot be hidden or shown.');
      return;
    }
    if (loadingHideUnhideRef.current != null) return;
    const nextHidden = !row.isHidden;
    setLoadingHideUnhideId(row.id);
    try {
      await updateEventLogVisibility({ id: row.id, is_hidden: nextHidden });
      SimpleToasterNotification(
        'success',
        nextHidden
          ? 'Event log hidden successfully.'
          : 'Event log is now visible.'
      );
      gridRef.current?.api?.refreshInfiniteCache();
    } catch {
      ErrorToasterNotification('Something went wrong. Please try again.');
    } finally {
      setLoadingHideUnhideId(null);
    }
  }, []);
  onHideUnhideRef.current = handleHideUnhide;

  useEffect(() => {
    const api = gridRef.current?.api;
    if (api) api.refreshCells({ columns: [HIDE_UNHIDE_COL_ID], force: true });
  }, [loadingHideUnhideId]);

  const refreshGrid = useCallback(() => {
    setTotalCount(null);
    const api = gridRef.current?.api;
    if (api) {
      const dataSource = new EventLogsDataSource({
        getFilters: () => filtersRef.current,
        onTotal: (total: number) => setTotalCount(total),
        getApi: () => gridRef.current?.api ?? undefined,
        onFetchStart: (startRow) => {
          if (startRow > 0) setLoadingMore(true);
        },
        onFetchEnd: () => setLoadingMore(false)
      });
      api.setGridOption('datasource', dataSource);
    }
  }, []);

  // When transferId is in URL, populate search input and search via transfer_id
  const hasInitializedSearchFromUrl = useRef(false);
  useEffect(() => {
    if (initialTransferId?.trim() && !hasInitializedSearchFromUrl.current) {
      setSearchInput(initialTransferId.trim());
      hasInitializedSearchFromUrl.current = true;
    }
  }, [initialTransferId]);

  useEffect(() => {
    refreshGrid();
  }, [
    refreshGrid,
    filters.transfer_id,
    filters.order_number,
    filters.action,
    filters.store_id,
    filters.dealerId,
    filters.showHidden,
    filters.search,
    filters.date_from,
    filters.date_to
  ]);

  // Load store list on mount so Add Event modal and filter dropdown have store options
  useEffect(() => {
    dispatch(
      callToGetStoreNames({
        orderBy: [],
        filter: ['is_external::inSet::true']
      })
    );
  }, [dispatch]);

  type FilterFormState = EventLogsFilterParams & { actionTypes?: string[] };
  const [filterForm, setFilterForm] = useState<FilterFormState>(() => ({}));
  const prevFilterOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isFilterOpen && !prevFilterOpenRef.current;
    prevFilterOpenRef.current = isFilterOpen;
    if (justOpened) {
      const actionTypes = filters.action
        ? filters.action.split(',').map((a) => a.trim().toLowerCase())
        : [];
      setFilterForm({ ...filters, actionTypes });
    }
  }, [isFilterOpen, filters]);

  useEffect(() => {
    const from = filterForm.date_from?.trim();
    const to = filterForm.date_to?.trim();
    if (from && to) {
      if (new Date(from) > new Date(to)) {
        setDateRangeError(INVALID_RANGE);
      } else {
        setDateRangeError('');
      }
    } else {
      if (from || to) {
        setDateRangeError(
          SELECT_FIELD_DATE.replace('{{field}}', !from ? 'start' : 'end')
        );
      } else {
        setDateRangeError('');
      }
    }
  }, [filterForm.date_from, filterForm.date_to]);

  // Close popover on outside click (but not when clicking calendar or trigger)
  useEffect(() => {
    if (!isFilterOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const el = target as Element;
      if (
        popoverContentRef.current?.contains(target) ||
        filterTriggerRef.current?.contains(target) ||
        el.closest?.('.flatpickr-calendar') ||
        el.closest?.('[class*="react-select"]') ||
        el.closest?.('[role="listbox"]')
      ) {
        return;
      }
      onCloseFilter();
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () =>
      document.removeEventListener('mousedown', handleMouseDown, true);
  }, [isFilterOpen, onCloseFilter]);

  const applyFilterChange = useCallback(
    (update: Partial<FilterFormState>) => {
      const nextForm = { ...filterForm, ...update };
      setFilterForm(nextForm);
      const actionVal = nextForm.actionTypes?.length
        ? nextForm.actionTypes.join(',')
        : undefined;
      const from = nextForm.date_from?.trim();
      const to = nextForm.date_to?.trim();
      const dateRangeValid =
        (!from && !to) ||
        (Boolean(from && to) && from && to && new Date(from) <= new Date(to));
      const next: EventLogsFilterParams = {
        ...filters,
        transfer_id: filters.transfer_id,
        order_number: filters.order_number,
        store_id: nextForm.store_id?.trim() || undefined,
        action: actionVal || undefined,
        showHidden: nextForm.showHidden,
        date_from: dateRangeValid ? from || undefined : filters.date_from,
        date_to: dateRangeValid ? to || undefined : filters.date_to
      };
      setFilters(withUrlDealerId(next));
    },
    [filterForm, filters, withUrlDealerId]
  );

  const clearAllFilters = useCallback(() => {
    const cleared: EventLogsFilterParams = {};
    setSearchInput('');
    if (initialDealerId?.trim()) cleared.dealerId = initialDealerId.trim();
    setFilters(withUrlDealerId(cleared));
    setFilterForm({ ...cleared, actionTypes: [] });
    setDateRangeError('');
    onCloseFilter();
  }, [onCloseFilter, initialDealerId, withUrlDealerId]);

  const ACTION_CHIP_LABELS: Record<string, string> = {
    system: 'System',
    called: 'Called',
    emailed: 'Emailed',
    'profile edit': 'Profile Edit',
    other: 'Other'
  };

  const filterChips = useMemo(() => {
    const chips: { fieldName: string; fieldValue: string; label: string }[] =
      [];
    if (filters.transfer_id?.trim()) {
      chips.push({
        fieldName: 'transferId',
        fieldValue: filters.transfer_id.trim(),
        label: `Transfer ID: ${filters.transfer_id.trim()}`
      });
    }
    if (filters.action) {
      filters.action.split(',').forEach((a) => {
        const v = a.trim().toLowerCase();
        if (v)
          chips.push({
            fieldName: 'action',
            fieldValue: v,
            label: ACTION_CHIP_LABELS[v] ?? v
          });
      });
    }
    if (filters.store_id && !isImpersonating) {
      const store = storeOptions.find(
        (o) => String(o.value) === String(filters.store_id)
      );
      chips.push({
        fieldName: 'store',
        fieldValue: String(filters.store_id),
        label: store?.label ?? filters.store_id
      });
    }
    if (filters.showHidden === true) {
      chips.push({
        fieldName: 'showHidden',
        fieldValue: 'true',
        label: 'Show hidden logs'
      });
    }
    if (filters.date_from || filters.date_to) {
      const from = filters.date_from ?? '';
      const to = filters.date_to ?? '';
      chips.push({
        fieldName: 'date',
        fieldValue: 'date',
        label: from && to ? `${from} - ${to}` : from || to
      });
    }
    return chips;
  }, [
    filters.transfer_id,
    filters.action,
    filters.store_id,
    filters.showHidden,
    filters.date_from,
    filters.date_to,
    storeOptions,
    isImpersonating
  ]);

  const handleRemoveTag = useCallback(
    (fieldName: string, fieldValue: string) => {
      if (fieldName === 'action') {
        const current = filters.action
          ? filters.action.split(',').map((a) => a.trim().toLowerCase())
          : [];
        const next = current.filter((v) => v !== fieldValue);
        const actionVal = next.length ? next.join(',') : undefined;
        const nextFilters = { ...filters, action: actionVal };
        const merged = withUrlDealerId(nextFilters);
        setFilters(merged);
        setFilterForm((p) => ({ ...p, actionTypes: next, ...merged }));
      } else if (fieldName === 'transferId') {
        const nextFilters: EventLogsFilterParams = {
          ...filters,
          transfer_id: undefined,
          ...(filters.search?.trim() === fieldValue ? { search: undefined } : {})
        };
        setFilters(withUrlDealerId(nextFilters));
        if (searchInput.trim() === fieldValue) {
          setSearchInput('');
        }
      } else if (fieldName === 'store') {
        const nextFilters = { ...filters, store_id: undefined };
        setFilters(withUrlDealerId(nextFilters));
        setFilterForm((p) => ({ ...p, store_id: undefined }));
      } else if (fieldName === 'showHidden') {
        const nextFilters = { ...filters, showHidden: undefined };
        setFilters(withUrlDealerId(nextFilters));
        setFilterForm((p) => ({ ...p, showHidden: undefined }));
      } else if (fieldName === 'date') {
        const nextFilters = {
          ...filters,
          date_from: undefined,
          date_to: undefined
        };
        setFilters(withUrlDealerId(nextFilters));
        setFilterForm((p) => ({
          ...p,
          date_from: undefined,
          date_to: undefined
        }));
      }
    },
    [filters, searchInput, withUrlDealerId]
  );

  const columnDefs: ColDef<DealerLogRow>[] = useMemo(
    () => [
      {
        field: 'transferId',
        headerName: 'Transfer ID',
        flex: 0,
        width: 110,
        cellRenderer: TransferLinkCell
      },
      {
        field: 'orderNumber',
        headerName: 'Order ID',
        flex: 0,
        width: 140
      },
      {
        field: 'time',
        headerName: 'Updated Date',
        flex: 0,
        width: 180,
        valueFormatter: (p) => formatTime(p.value) || EMPTY_PLACEHOLDER
      },
      {
        field: 'updatedBy',
        headerName: 'Updated By',
        flex: 0,
        width: 150
      },
      {
        field: 'action',
        headerName: 'Action',
        flex: 0,
        width: 130,
        cellRenderer: ActionBadgeCell
      },
      {
        field: 'notes',
        headerName: 'Notes',
        flex: 1,
        minWidth: 200,
        cellClass: 'dealer-logs-notes-cell',
        cellRenderer: NotesCell
      },
      {
        field: 'store',
        headerName: 'Store',
        flex: 0,
        width: 140
      },
      {
        colId: HIDE_UNHIDE_COL_ID,
        headerName: 'Action',
        flex: 0,
        width: 100,
        sortable: false,
        cellClass: 'dealer-logs-action-btn-cell',
        cellRenderer: HideUnhideActionCell,
        cellRendererParams: {
          getOnToggle: () => onHideUnhideRef.current,
          loadingId: loadingHideUnhideId
        }
      }
    ],
    [loadingHideUnhideId]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      disableCellTextSelection: true,
      suppressHeaderMenuButton: true
    }),
    []
  );

  const onGridReady = useCallback((params: GridReadyEvent<DealerLogRow>) => {
    const dataSource = new EventLogsDataSource({
      getFilters: () => filtersRef.current,
      onTotal: (total: number) => setTotalCount(total),
      getApi: () => gridRef.current?.api ?? undefined,
      onFetchStart: (startRow) => {
        if (startRow > 0) setLoadingMore(true);
      },
      onFetchEnd: () => setLoadingMore(false)
    });
    params.api.setGridOption('datasource', dataSource);
  }, []);

  const gridOptions: GridOptions<DealerLogRow> = useMemo(
    () => ({
      rowModelType: 'infinite',
      infiniteInitialRowCount: 1,
      autoSizeStrategy: {
        type: 'fitGridWidth'
      },
      maxConcurrentDatasourceRequests: 1,
      cacheBlockSize: PAGE_SIZE,
      cacheOverflowSize: 1,
      columnDefs,
      defaultColDef,
      animateRows: true,
      suppressRowClickSelection: true,
      suppressCellFocus: true,
      onGridReady,
      icons: agGridIconsConfig,
      overlayNoRowsTemplate:
        '<div class="not-found-box"><img src="/images/ui/not-found-icon.png"></img><p class="not-found-box__title">No action logs found</p></div>',
      getRowId: (params) =>
        params.data.id != null
          ? String(params.data.id)
          : params.data.transferId + params.data.time
    }),
    [columnDefs, defaultColDef, onGridReady]
  );

  const hasTransferId = Boolean(initialTransferId?.trim());

  return (
    <Box className="dealer-logs-container">
      <Breadcrumb
        spacing="3px"
        separator={<ChevronRightIcon className="right-arrow" color="#323232" />}
      >
        <BreadcrumbItem className="breadcrumb-item">
          <BreadcrumbLink
            className="breadcrumb-link"
            as={Link}
            to={hasTransferId ? '/transfers' : '/dealers-search'}
          >
            {hasTransferId ? 'Transfer Queue' : 'Search'}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem className="breadcrumb-item">
          <BreadcrumbLink
            className="breadcrumb-link"
            as={Link}
            to={
              hasTransferId
                ? `/transfers/${initialTransferId}`
                : initialDealerId
                ? `/dealers-search/${initialDealerId}`
                : '/dealers'
            }
          >
            {hasTransferId ? 'Transfer Detail' : 'Dealer Details'}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem className="breadcrumb-item">
          <span className="breadcrumb-link breadcrumb-text">Activity Logs</span>
        </BreadcrumbItem>
      </Breadcrumb>
      <Box className="dealer-logs-search-wrapper" mb={0}>
        <form
          className="dealer-logs-search-form"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = searchInput.trim();
            const keepTransferId =
              trimmed &&
              initialTransferId?.trim() &&
              trimmed === initialTransferId.trim();
            setFilters((prev) =>
              withUrlDealerId({
                ...prev,
                search: trimmed || undefined,
                transfer_id: keepTransferId ? initialTransferId!.trim() : undefined
              })
            );
          }}
        >
          <FormControl
            whiteSpace="nowrap"
            padding={0}
            className="dealer-logs-search-form-control"
          >
            <InputGroup className="input-group">
              <Input
                placeholder="Search by transfer, order, store or updated by..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                height="40px"
                className="dealer-logs-search-input"
              />
              <InputRightElement
                width="4.5rem"
                className="input-right-element"
                alignItems="center"
              >
                <Divider
                  orientation="vertical"
                  className="input-right-divider"
                />
                <Button type="submit" size="sm" backgroundColor="#FFFFFF">
                  <Image src="/images/ui/searchIcon.svg" alt="" />
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>
          <Text
            className="dealer-log-results-count"
            sx={{ visibility: totalCount != null ? 'visible' : 'hidden' }}
          >
            {totalCount != null ? `Found ${totalCount} Results` : '-'}
          </Text>
        </form>
        <Box className="dealer-log-filter-actions-wrapper">
          <Popover
            isOpen={isFilterOpen}
            onOpen={onOpenFilter}
            onClose={onCloseFilter}
            placement="bottom-start"
            strategy="fixed"
            closeOnBlur={false}
          >
            <PopoverTrigger>
              <Button
                ref={filterTriggerRef}
                className="dealer-log-filter-by-button"
                type="button"
              >
                <Image src="/images/ui/filter.svg" />
                <Text>Filter By</Text>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="dealer-logs-filter-popover"
              width="auto"
              minW="520px"
              maxW="840px"
              _focus={{ boxShadow: 'md' }}
            >
              <PopoverBody className="dealer-logs-filter-popover-body">
                <Box
                  ref={popoverContentRef}
                  className="dealer-logs-filter-grid-wrapper"
                >
                  <Box
                    className={`dealer-logs-filter-grid${
                      isImpersonating ? ' dealer-logs-filter-grid--3-cols' : ''
                    }`}
                  >
                    <Box className="dealer-logs-filter-col dealer-logs-filter-col-action">
                      <Text
                        fontSize={'18px'}
                        fontWeight={700}
                        lineHeight={'21.6px'}
                      >
                        ACTION TYPE
                      </Text>
                      <Box marginTop={'15px'}>
                        {Object.keys(dealerLogActionTypeFilter).map(
                          (ele: string, index: number) => (
                            <RenderItems
                              key={ele}
                              item={
                                dealerLogActionTypeFilter[
                                  ele as keyType<
                                    typeof dealerLogActionTypeFilter
                                  >
                                ]
                              }
                              value={ele}
                              index={index}
                              handler={(isChecked, value) => {
                                const next = isChecked
                                  ? (filterForm.actionTypes ?? []).filter(
                                      (a) => a !== value
                                    )
                                  : [...(filterForm.actionTypes ?? []), value];
                                applyFilterChange({ actionTypes: next });
                              }}
                              checked={(filterForm.actionTypes ?? []).includes(
                                ele
                              )}
                            />
                          )
                        )}
                      </Box>
                    </Box>
                    <Box className="dealer-logs-filter-col dealer-logs-filter-col-hidden">
                      <Text
                        fontSize={'18px'}
                        fontWeight={700}
                        lineHeight={'21.6px'}
                      >
                        HIDDEN LOGS
                      </Text>
                      <Box marginTop={'15px'}>
                        <RenderItems
                          item="Include Hidden Logs"
                          value="showHidden"
                          index={0}
                          handler={(isChecked) => {
                            applyFilterChange({
                              showHidden: isChecked ? undefined : true
                            });
                          }}
                          checked={filterForm.showHidden === true}
                        />
                      </Box>
                    </Box>
                    {!isImpersonating && (
                      <Box className="dealer-logs-filter-col dealer-logs-filter-col-store">
                        <Text
                          fontSize={'18px'}
                          fontWeight={700}
                          lineHeight={'21.6px'}
                        >
                          {constants.STORE_NAME}
                        </Text>
                        <Box marginTop={'15px'}>
                          <Select
                            options={storeOptions}
                            value={
                              storeOptions.find(
                                (o: { value: string }) =>
                                  String(o.value) ===
                                  String(filterForm.store_id ?? '')
                              ) ?? null
                            }
                            onChange={(opt) =>
                              applyFilterChange({
                                store_id:
                                  opt?.value != null
                                    ? String(opt.value)
                                    : undefined
                              })
                            }
                            getOptionLabel={(o: { label: string }) => o.label}
                            getOptionValue={(o: { value: string }) =>
                              String(o.value)
                            }
                            isClearable
                            placeholder="Select..."
                            menuPortalTarget={
                              typeof document !== 'undefined'
                                ? document.body
                                : null
                            }
                            menuPosition="fixed"
                            menuPlacement="auto"
                            styles={{
                              control: (base) => ({ ...base, minHeight: 36 }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 })
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    <Box
                      ref={setDatePickerContainer}
                      className="dealer-logs-filter-col dealer-logs-filter-col-date"
                      position="relative"
                    >
                      <Text
                          fontSize={'18px'}
                          fontWeight={700}
                          lineHeight={'21.6px'}
                        >
                          DATE
                        </Text>
                      <Box
                        display="flex"
                        gap={2}
                        alignItems="flex-start"
                        flexWrap="wrap"
                        marginTop={'15px'}
                      >
                        <div className="date-range-wrapper__item_queue">
                          <FlatDatePickr
                            label="From Date"
                            value={
                              parseYYYYMMDDToDate(filterForm.date_from) ?? null
                            }
                            onDateChange={(date: Date) =>
                              applyFilterChange({
                                date_from: formatDateToYYYYMMDD(date)
                              })
                            }
                            options={
                              datePickerContainer
                                ? { appendTo: datePickerContainer }
                                : undefined
                            }
                          />
                        </div>
                        <div className="date-range-wrapper__item_queue">
                          <FlatDatePickr
                            label="To Date"
                            value={
                              parseYYYYMMDDToDate(filterForm.date_to) ?? null
                            }
                            onDateChange={(date: Date) =>
                              applyFilterChange({
                                date_to: formatDateToYYYYMMDD(date)
                              })
                            }
                            options={{
                              ...(datePickerContainer
                                ? { appendTo: datePickerContainer }
                                : {}),
                              ...(parseYYYYMMDDToDate(filterForm.date_from)
                                ? {
                                    minDate:
                                      parseYYYYMMDDToDate(
                                        filterForm.date_from
                                      ) ?? undefined
                                  }
                                : {})
                            }}
                          />
                        </div>
                      </Box>
                      {dateRangeError && (
                        <div className="date-range-error">
                          <span className="transfer-filter-error">
                            {dateRangeError}
                          </span>
                        </div>
                      )}
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" mt={4} pt={3}>
                    <Button
                      variant="link"
                      color="#0090c6"
                      fontWeight="600"
                      fontSize="sm"
                      onClick={() => clearAllFilters()}
                    >
                      CLEAR ALL
                    </Button>
                  </Box>
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Popover>
          <Button
            className="dealer-log-add-event-button"
            onClick={onOpenAddEvent}
          >
            <Image src="/images/ui/white-add.svg" />
            <Text>Add an Event</Text>
          </Button>
        </Box>
      </Box>

      <Box
        className="outer-clips-box"
        sx={{ width: '100%', marginBottom: '0 !important' }}
      >
        <Box className="left-inner-clips-box">
          {filterChips.map((chip, index) => (
            <Tag
              key={`${chip.fieldName}-${chip.fieldValue}-${index}`}
              className="chip-box"
            >
              <TagLabel className="tag-label">{chip.label}</TagLabel>
              <TagCloseButton
                padding="0px"
                fontSize="15px"
                color="#436175"
                onClick={() => handleRemoveTag(chip.fieldName, chip.fieldValue)}
              />
            </Tag>
          ))}
        </Box>
        <Box className="right-inner-clips-box">
          {filterChips.length > 0 && (
            <button
              type="button"
              className="clear-all-btn"
              onClick={() => clearAllFilters()}
            >
              Clear All
            </button>
          )}
        </Box>
      </Box>

      <Box className="dealer-logs-table-container" position="relative">
        <div style={gridStyle} className="ag-theme-alpine dealer-logs-ag-grid">
          <AgGridReact<DealerLogRow>
            className={'ag-theme-alpine'}
            ref={gridRef}
            gridOptions={gridOptions}
          />
        </div>
      </Box>

      <AddEventModal
        isOpen={isAddEventOpen}
        onClose={onCloseAddEvent}
        onSuccess={refreshGrid}
        initialLicenseKey={initialLicenseKey}
      />
    </Box>
  );
};

export default DealerLogs;
